import { createApp, reactive } from 'vue'
import App from './App.vue'

// The __VUE_PROD_HYDRATION_MISMATCH_DETAILS__ variable is a Vue.js configuration 
// option that allows you to control the behavior of server-side rendering (SSR) 
// hydration mismatch errors in production environments.
window.__VUE_PROD_HYDRATION_MISMATCH_DETAILS__ = true;

import teoweb from 'teoweb' // to use teonet webrtc connection
import 'bootstrap/dist/css/bootstrap.css' // to use bootstrap CSS
import 'bootstrap/dist/js/bootstrap.bundle.js' // to use bootstrap JS
import 'bootstrap-icons/font/bootstrap-icons.css'; // to use bootstrap icons
import './bootstrap_theme_tolge.js' // to use bootstrap color theme tolge
import './registerServiceWorker'

// Server name and server name websocket
let server_name = "";
let server_name_ws = "";
let server_name_wt = "";

// Get server name from server_name.js or default
try {
    let s = require('./server_name.js')
    // 
    console.debug("got server_name from file:", s.server_name)
    server_name = s.server_name
    server_name_ws = s.server_name_ws
    server_name_wt = s.server_name_wt
} catch (error) {
    //
    console.debug("got server_name default:", server_name)
}

const app = createApp(App)

// Connect type
const connectType = localStorage.getItem("connectType") || "ws";
console.debug("connect type:", connectType);

// Vue global reactive state
const globalState = reactive({
    apiUrl: "/api/v1", // api URL will be set once after connection
    accessAdmin: false, // allow admin access
    accessDevel: false, // allow developer access
    selectedRecipes: [], // Array to hold IDs of recipes selected for cooking. Will be populated from server on login.
    // Holds the state of the last search on the main page to restore it when navigating back.
    lastSearchState: {
        searchTerm: null,
        results: null,
        scrollPosition: 0,
    },
    analytics: null,
});

// Vue global properties
app.config.globalProperties.$globalState = globalState;
app.config.globalProperties.teoweb = teoweb(connectType);

// Server name webrtc and server name websocket
app.config.globalProperties.server_name = server_name;
app.config.globalProperties.server_name_ws = server_name_ws;
app.config.globalProperties.server_name_wt = server_name_wt;

/**
 * Reset teoweb connection.
 * 
 * This function resets teoweb connection, so, it will reconnect to the server
 * with the given connectType.
 * 
 * @param {string} [connectType="webrtc"] - connection type, "webrtc" or "websocket"
 */
app.config.globalProperties.reset = (connectType = "webrtc") => {
    app.config.globalProperties.teoweb = teoweb(connectType);
}

/**
 * Wait when teoweb connected to webrtc server and login token was checked.
 * 
 * @param {number} timeout - timeout in ms, default 0
 * @returns {Promise} - resolves when teoweb connected and login token was checked
 */
app.config.globalProperties.whenLogin = (timeout = 0) => {
    return new Promise((resolve, reject) => {
        const teoweb = app.config.globalProperties.teoweb;
        const started = performance.now();

        const check = () => {
            if (teoweb.token != null) {
                // if token is not null, resolve the promise
                resolve();
                return;
            }
            if (timeout && performance.now() - started >= timeout) {
                // if timeout is exceeded, reject the promise
                reject();
                return;
            }
            // call check again on next animation frame
            requestAnimationFrame(check);
        }
        // start check
        check();
    })
};

/**
 * Wait until webasm is ready.
 * 
 * @param {number} timeout - timeout in ms, default 0
 * @returns {Promise} - resolves when webasm is ready
 */
app.config.globalProperties.webasmReady = (timeout = 0) => {
    return new Promise((resolve, reject) => {
        const started = performance.now();

        const check = () => {
            if (window.decompressData) {
                // if webasm is ready, resolve the promise
                resolve();
                return;
            }
            if (timeout && performance.now() - started >= timeout) {
                // if timeout is exceeded, reject the promise
                reject();
                return;
            }
            // call check again on next animation frame
            requestAnimationFrame(check);
        }
        // start check
        check();
    })
};

/**
 * Send teoweb command and wait answer from server or timeout, when connected
 * and login.
 * 
 * If not connected and not logged in, wait until connected or reject with
 * timeout error.
 * 
 * @param {string} cmd - teoweb command
 * @param {object} [data=null] - data to send with command
 * @param {number} [timeout=15000] - timeout in ms
 * @returns {Promise} - resolves with answer data or rejects with error
 */
app.config.globalProperties.sendCmd = (cmd, data = null, timeout = 60000) => {
    const teoweb = app.config.globalProperties.teoweb;
    const whenLogin = app.config.globalProperties.whenLogin;

    // The proccessed flag will be set to true when teoweb answer will be 
    // received
    let processed = false;
    let error = null;

    // Create and add reader which will receive data from WebRTC Data Channel
    const reader = teoweb.addReader(function (gw, d) {
        if (gw.command == cmd) {
            data = d;
            if (gw.err) {
                error = gw.err;
            }
            processed = true;
        }
    });

    // Delete reader function
    const delReader = () => teoweb.delReader(reader);

    // Return promise which will be resolved when login done or timeout and
    // teoweb answer will be received
    return new Promise((resolve, reject) => {

        console.debug("sendCmd:", cmd);

        const timeout_error = "answer timeout";
        const timeout_login_error = "login timeout";

        // Wait login done
        whenLogin(timeout).then(
            // Login success
            () => {
                const started = performance.now();

                // Send request with command and data to WebRTC serve
                teoweb.sendCmd(cmd, data);

                const check = () => {
                    if (processed) {
                        delReader();
                        if (error) {
                            reject(error);
                        } else {
                            resolve(data);
                        }
                        return;
                    }
                    if (timeout && performance.now() - started >= timeout) {
                        delReader();
                        reject(timeout_error);
                        return;
                    }
                    requestAnimationFrame(check);
                }
                check();
            },

            // Login failed
            () => {
                delReader();
                reject(timeout_login_error);
            }
        ).catch((err) => {
            delReader();
            reject(err);
        });
    })
};

/**
 * Wait when teoweb connected or reconnected and than call callback function.
 *
 * @param {function} f - function to call when teoweb connected or reconnected
 * @returns {object} - object with stop method
 */
app.config.globalProperties.whenConnected = (f) => {

    let teoweb = app.config.globalProperties.teoweb;

    let checkConnected = true;
    let stopCheck = false;

    // Check teoweb connected flag
    const check = () => {
        // Stop check condition if stopCheck is true
        if (stopCheck) {
            return;
        }

        // Check connected
        const connected = teoweb.connected();
        if (checkConnected && connected) { // && teoweb.token != null) {
            // Connected
            checkConnected = false;
            f();
        } else if (!checkConnected && !connected) {
            // Disconnected
            checkConnected = true;
            teoweb.token = null;
        }

        requestAnimationFrame(check);
    }

    // Start check
    check();

    // Return object with stop method
    return {
        /**
         * Stop check teoweb connected flag
         */
        stop: () => {
            stopCheck = true;
        }
    }
};

// Wait when teoweb connected and login and than call callback function.
app.config.globalProperties.whenConnectedLogin = (f) => {
    let whenConnected = app.config.globalProperties.whenConnected;
    let whenLogin = app.config.globalProperties.whenLogin;
    whenConnected(() => whenLogin().then(f));
}

/**
 * Unpacks data received from Teonet using window.decompressData.
 * This method is similar to the one in RecipeList.vue.
 * @param {string} base64PackedData - The base64 encoded packed string from the server.
 * @returns {Promise<string>} - A promise that resolves with the unpacked JSON string.
 */
app.config.globalProperties.unpackData = (base64PackedData) => {
    const webasmReady = app.config.globalProperties.webasmReady;
    return new Promise((resolve, reject) => {
        webasmReady()
            .then(() => {
                try {
                    const unpackedData = window.decompressData(base64PackedData);
                    resolve(unpackedData);
                } catch (e) {
                    console.error(
                        "Error in window.decompressData:", e,
                        "Input data:", base64PackedData
                    );
                    reject(e);
                }
            })
            .catch(reject);
    });
}

app.mount('#app')
